import React from 'react';
import { ChevronLeft, Clock, Home } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import Upgrade from '../../upgrade/Upgrade';
import UpgradeSuccessModal from '../../upgrade/UpgradeSuccessModal';
import { useSubscriptionManagement } from '../../../queries';
import { useUserFormData } from '../../../hooks';
import UserDetailsViewer from '../../userDetails/UserDetailsViewer';
import HeaderProfileMenu from './HeaderProfileMenu';
import {
  ASK_ME_QUESTIONS_URL,
  LEARN_A_TOPIC_URL,
  PRACTICE_QUESTIONS_URL
} from '../../../constants';

function timeToSeconds({ hours = '00', minutes = '00', seconds = '00' } = {}) {
  return (
    parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(seconds, 10)
  );
}

function formatTime(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function getTimerStyle(totalSeconds) {
  if (totalSeconds <= 60) {
    return {
      containerClass: 'bg-red-100 border border-red-200',
      iconClass: 'text-red-600',
      textClass: 'text-red-700 font-semibold'
    };
  }
  if (totalSeconds <= 300) {
    return {
      containerClass: 'bg-amber-100 border border-amber-200',
      iconClass: 'text-amber-600',
      textClass: 'text-amber-700 font-medium'
    };
  }
  return {
    containerClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
    textClass: 'text-foreground font-medium'
  };
}

const AppHeader = ({
  title,
  time,
  pageTitle,
  onBadgeOpen,
  headerRightRenderer,
  welcomeMode = false,
  testMode = false,
  showHomeButton = false,
  showBackButton = false,
  onBack
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: managementUrls } = useSubscriptionManagement();
  const {
    userFormDetails,
    isDetailsModalOpen,
    openDetailsModal,
    closeDetailsModal,
    hasFormDetails
  } = useUserFormData();

  const isTestMode = Boolean(time) || testMode;
  // Test mode is also used by untimed assignments and the review screen, so the countdown
  // must key off an actual time value instead — otherwise it renders a red 0:00.
  const hasTimer = Boolean(time);
  const hasPageTitle = Boolean(pageTitle);
  const chatRoots = [PRACTICE_QUESTIONS_URL, ASK_ME_QUESTIONS_URL, LEARN_A_TOPIC_URL];
  const isChatThread = chatRoots.some((path) => location.pathname.startsWith(`${path}/`));
  const isChatSection = chatRoots.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const isAssignmentFlowPage =
    location.pathname.startsWith('/start') || location.pathname.startsWith('/assignment');
  const isJoinPage = location.pathname === '/join';
  const showUpgradeButton =
    !isTestMode && !isChatSection && !isAssignmentFlowPage && !isJoinPage;
  const profileMenuVariant = welcomeMode || isAssignmentFlowPage ? 'welcome' : 'full';

  const onJoin = () => navigate('/join');
  const handleMyPlan = () => {
    if (managementUrls?.cancel) {
      window.open(managementUrls.cancel);
    }
  };

  const getChatParentPath = () => {
    const matchedRoot = chatRoots.find((path) => location.pathname.startsWith(`${path}/`));
    return matchedRoot || '/';
  };

  const handleChatBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(getChatParentPath());
  };

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (isChatThread) {
      handleChatBack();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  const showMobileBack = showBackButton || isChatThread;

  const totalSeconds = time ? timeToSeconds(time) : 0;
  const timerStyle = getTimerStyle(totalSeconds);

  return (
    <header
      className={`relative z-10 border-b border-border bg-white px-[14px] pb-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] ${
        isTestMode ? 'fixed left-0 right-0 top-0 z-50' : ''
      }`}
    >
      <div
        className={`flex min-h-10 items-center gap-2 ${
          isChatThread
            ? 'grid grid-cols-[2.25rem_minmax(0,1fr)_2.75rem] md:flex md:justify-between'
            : 'justify-between'
        }`}
      >
        {/* Left: back / home / logo */}
        <div
          className={`flex min-w-0 items-center gap-2 ${
            isChatThread ? 'justify-self-start md:flex-1' : 'md:flex-1'
          }`}
        >
          {showMobileBack && (
            <button
              type="button"
              onClick={handleBack}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:hidden"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {showHomeButton && (
            <Link
              to="/"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Go to home"
            >
              <Home className="h-5 w-5" />
            </Link>
          )}
          {isTestMode && typeof title === 'string' && (
            <h1 className="max-w-[100px] truncate text-sm font-medium text-foreground md:max-w-md md:text-lg">
              {title}
            </h1>
          )}
          {!isTestMode && !isChatThread && (
            <Link
              to="/"
              className={`rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                showBackButton ? 'hidden md:inline-flex' : ''
              }`}
              aria-label="Classwise home"
            >
              <img src="/assets/images/logo.png" alt="Classwise" className="h-8 object-contain" />
            </Link>
          )}
          {!isTestMode && isChatThread && (
            <Link
              to="/"
              className="hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:inline-flex"
              aria-label="Classwise home"
            >
              <img src="/assets/images/logo.png" alt="Classwise" className="h-8 object-contain" />
            </Link>
          )}
        </div>

        {/* Center: timer during test */}
        {hasTimer && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            role="timer"
            aria-live="polite"
            aria-label={`Time remaining: ${formatTime(totalSeconds)}`}
          >
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1 transition-all duration-300 tabular-nums ${timerStyle.containerClass}`}
            >
              <Clock className={`h-4 w-4 transition-colors duration-300 ${timerStyle.iconClass}`} />
              <span className={`text-sm font-medium transition-colors duration-300 ${timerStyle.textClass}`}>
                {formatTime(totalSeconds)}
              </span>
            </div>
          </div>
        )}

        {hasPageTitle && !isTestMode && (
          <div
            className={`flex min-w-0 items-center justify-center px-1 text-center ${
              isChatThread
                ? 'self-center'
                : 'pointer-events-none absolute left-1/2 top-1/2 max-w-[min(60%,calc(100%-11rem))] -translate-x-1/2 -translate-y-1/2 md:max-w-[50%]'
            } ${isChatThread ? 'md:pointer-events-none md:absolute md:left-1/2 md:top-1/2 md:max-w-[50%] md:-translate-x-1/2 md:-translate-y-1/2' : ''}`}
          >
            {typeof pageTitle === 'string' ? (
              <p className="truncate text-sm font-semibold text-foreground md:text-lg">
                {pageTitle}
              </p>
            ) : (
              pageTitle
            )}
          </div>
        )}

        {/* Right: Upgrade + profile */}
        <div
          className={`flex items-center justify-end gap-2.5 ${
            isChatThread ? 'justify-self-end self-center md:flex-1' : 'md:flex-1'
          }`}
        >
          <Upgrade showButton={showUpgradeButton} />
          {headerRightRenderer?.()}
          <HeaderProfileMenu
            user={user}
            signOut={signOut}
            onJoin={onJoin}
            onBadgeOpen={onBadgeOpen}
            onMyPlan={handleMyPlan}
            onOpenDetails={openDetailsModal}
            hasFormDetails={hasFormDetails}
            showMyPlan={Boolean(managementUrls?.cancel)}
            variant={profileMenuVariant}
          />
        </div>
      </div>

      <UpgradeSuccessModal />
      {isDetailsModalOpen && (
        <UserDetailsViewer
          userDetails={userFormDetails}
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
        />
      )}
    </header>
  );
};

export default AppHeader;
